import m from "mithril";
import _ from "underscore";
import $ from "jquery";

import {FormView} from "./formView";
import Header from "./components/header";
import {Questionary} from "../model/questionary";
import Messages from "../model/messages";
import preload from "./preload";
import {Vnode} from "mithril";
import {LangView} from "./langView";
import FinishedSaving from "./components/FinishedSaving";
import FeedbackView from "./feedback/feedbackView";
import TFeedbackDefinition from "../model/feedback/TFeedbackDefinition";

type Visibility = "show"|"hide";

interface AppOptions {
	element: string|Node,
	showProgressbar: boolean,
	showQuestionNumber: boolean,
	pageAnimation: string,
	messages: Messages,
	forceVisibility?: {
		nextButton?: Visibility,
		backButton?: Visibility
	},
    feedback?: TFeedbackDefinition,
}

export class Application {
    messages: Messages;
    private questionary: Questionary;
    private formView: FormView|null = null;
    public header: Header;
    public readonly finishedSaving: FinishedSaving;
    private langView : LangView|null = null;
    private feedback : TFeedbackDefinition|null = null;
    private feedbackView : FeedbackView|null = null;
    private pageAnimation: string;
    public readonly showProgressbar: boolean;
    public readonly showQuestionNumber: boolean;
    private movingBack: boolean = false;
    private forceVisibility: { nextButton?: Visibility, backButton?: Visibility };

    private finishedEventSent = false;
    public self: Application;

    public static run(questionary: Questionary, options: AppOptions){
        const element: Element = _.isString(options.element) ? $(options.element)[0] : <Element>options.element;

        const app = new Application(questionary, options);
        m.mount(
            element || document.body,
            {
                view: (vnode: Vnode) => {
                    return m(app, vnode.attrs);
                }
            }
        );
        m.redraw();

        return app;
    };

    private constructor(questionary: Questionary, options: AppOptions) {
        this.questionary = questionary;
        this.messages = options.messages;
        this.pageAnimation = options.pageAnimation;
        this.showProgressbar = options.showProgressbar;
        this.showQuestionNumber = options.showQuestionNumber;
        this.forceVisibility = options.forceVisibility || {};
        this.feedback = options.feedback ?? null;
        this.header = new Header(questionary);
        this.finishedSaving = new FinishedSaving(questionary);
        this.self = this;
        $(document).keyup((e:KeyboardEvent) => {
            if(this.formView){
                this.formView.globalKeyPressed(e);
            }
        });

        // handle browser back button, disable next. (only for browsers with history api support http://caniuse.com/#feat=history)
        if(history.pushState){

            history.pushState(-1, '', window.location.href);
            history.pushState(0, '', window.location.href);

            window.addEventListener('popstate', event => {
                if(event.state === -1){
                    history.pushState(0, '', window.location.href);
                    if(this.prevEnabled()){
                        this.prev();
                        m.redraw();
                    }
                }
            });
        }
        this.questionary.on('submitForm', () => this.next());

        /* Experimental preload resources over all forms */
        this.questionary.getFormList().forEach((form) => {
            preload(form.resourcesToPreload());
        });

        this.updateFormView();
    }

    view() {
        this.updateFormView();

        let component;
        const feedbackView = this.getFeedbackView();
        if (this.questionary.dictionary && !this.questionary.dictionary.locale) {
            component = m(this.getLangView());
        } else if (this.formView) {
            component = [
                m(this.formView),
            ];
        } else if (
            // current form is not set
            !this.questionary.currentForm
            // and feedback exists, but it is not complete
            && feedbackView && !feedbackView.isFinished()
            // and questionary is FINISHED or/and questionary saving is in process, but not with *OUT status
            && (this.questionary.state.status === 'FINISHED' || (this.questionary.state.status === null && ['saving', 'problems', 'failed', 'saved_withStatus'].includes(this.questionary.savingStatus)) )
        ) {
            component = m(feedbackView);
        } else {
            component = [
                m(this.finishedSaving),
            ];
        }

        return m(this.getHtmlClass(), [component]);
    }

    onupdate(): void {

        const triggerIt = () => {
            this.questionary.trigger(Questionary.Events.QUESTIONARY_FINISHED);
            this.self.finishedEventSent = true;
        }

        // Trigger once QUESTIONARY_FINISHED when app meets conditions
        if (this.finishedEventSent) {
            // this event was already sent
            return;
        }
        if (this.questionary.savingStatus !== "saved_withStatus") {
            // questionary is not saved yet
            return;
        }
        if (!this.feedback) {
            // feedback definition missing - skip feedback
            return triggerIt();
        }
        if (this.questionary.state.status && this.questionary.state.status !== 'FINISHED') {
            // questionary status is some kind of screenout - skip feedback
            return triggerIt();
        }
        if (this.getFeedbackView()?.isFinished()) {
            // feedback is finished
            return triggerIt();
        }
    }

    private getHtmlClass(): string {
        const customForm = this.questionary.currentForm ? this.questionary.currentForm.customForm : null;

        return '.nms-survey'+(customForm ? '.isCustomForm' : '.isNotCustomForm')+
            (this.showQuestionNumber ? '.showQuestionNumber' : '')+
            (this.questionary.currentForm ? '.form-'+this.questionary.currentForm.id : '')+
            (this.questionary.dictionary?.locale ? `.form-lang-${this.questionary.dictionary?.locale}` : '')+
            (this.questionary.currentForm && !customForm ? '.'+this.questionary.currentForm.class : '');
    }

    next() {
        if (this.questionary.next()) {
            this.movingBack = false;
            this.questionary.scrollTop();
        }
    };

    /**
     * Update formView from Questionary
     * @returns true if currentForm has changed
     */
    private updateFormView() {
        let previous = this.self.formView;
        if (!this.self.formView || !this.self.formView.isCurrentForm()) {
            this.self.formView = this.self.questionary.currentForm ? new FormView(this.self.questionary, this.self.questionary.currentForm, this) : null;
        }
        return previous !== this.self.formView;
    };

    private getLangView() {
        if (!this.langView) {
            this.langView = new LangView(this, this.questionary);
        }
        return this.langView;
    }

    /**
     * Create and get feedback view, if feedback definition is provided
     */
    private getFeedbackView(): FeedbackView|null {
        if (!this.feedbackView && this.feedback) {
            this.feedbackView = new FeedbackView(this.feedback, this.questionary);
        }
        return this.feedbackView;
    }

    private isForceVisibility(key: "nextButton"|"backButton"): boolean|undefined {
        return ( !this.forceVisibility || !this.forceVisibility[key]) ? undefined : this.forceVisibility[key] === 'show';
    };

    public nextEnabled() {
        const forceVisibility = this.isForceVisibility("nextButton");
        if (forceVisibility !== undefined) {
            return forceVisibility;
        }
        return this.questionary.currentForm && this.questionary.currentForm.nextButtonVisible && !this.questionary.currentForm.blockNext();
    };

    private prev() {
        this.questionary.prev();
        this.movingBack = true;
        //this.scrollTop();
    };

    public prevEnabled() {
        const forceVisibility = this.isForceVisibility("backButton");
        const visible = (forceVisibility !== undefined) ? forceVisibility : (this.questionary.currentForm && this.questionary.currentForm.backButtonVisible);
        const isFirstForm = this.questionary.getFirstForm() === this.questionary.currentForm;
        return !isFirstForm && visible;
    };

    public langSwitcherEnabled(): boolean {
        return Boolean(this.questionary.dictionary && this.questionary.dictionary.locales() && this.questionary.dictionary.locales().length > 1);
    }
}
